import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import type { CrawlSchedule, WithResourceId } from '../../../types/knowledge';
import type { CrawlScheduleFormValues } from '../crawlScheduleForm';
import {
  useCreateKnowledgeCrawlScheduleMutation,
  useDeleteKnowledgeCrawlScheduleMutation,
  useUpdateKnowledgeCrawlScheduleMutation,
} from '../mutations/useKnowledgeCrawlMutations';
import { useKnowledgeCrawlsTabStore } from '../stores/useKnowledgeCrawlsTabStore';

type UseCrawlSchedulesOptions = {
  databaseId: string;
  crawlScheduleByJobId: Map<string, WithResourceId<CrawlSchedule>>;
};

export function useCrawlSchedules({
  databaseId,
  crawlScheduleByJobId,
}: UseCrawlSchedulesOptions) {
  const { translate } = useI18n();
  const createCrawlScheduleMutation = useCreateKnowledgeCrawlScheduleMutation();
  const updateCrawlScheduleMutation = useUpdateKnowledgeCrawlScheduleMutation();
  const deleteCrawlScheduleMutation = useDeleteKnowledgeCrawlScheduleMutation();

  const scheduleModalCrawl = useKnowledgeCrawlsTabStore(
    (state) => state.scheduleModalCrawl,
  );
  const isScheduleSubmitting = useKnowledgeCrawlsTabStore(
    (state) => state.isScheduleSubmitting,
  );
  const pendingScheduleDelete = useKnowledgeCrawlsTabStore(
    (state) => state.pendingScheduleDelete,
  );
  const openScheduleModal = useKnowledgeCrawlsTabStore(
    (state) => state.openScheduleModal,
  );
  const closeScheduleModal = useKnowledgeCrawlsTabStore(
    (state) => state.closeScheduleModal,
  );
  const setIsScheduleSubmitting = useKnowledgeCrawlsTabStore(
    (state) => state.setIsScheduleSubmitting,
  );
  const setPendingScheduleDelete = useKnowledgeCrawlsTabStore(
    (state) => state.setPendingScheduleDelete,
  );

  const scheduleModalExisting = scheduleModalCrawl
    ? (crawlScheduleByJobId.get(scheduleModalCrawl.id) ?? null)
    : null;

  const handleCloseScheduleModal = () => {
    if (isScheduleSubmitting) {
      return;
    }
    closeScheduleModal();
  };

  const handleSaveSchedule = (values: CrawlScheduleFormValues) => {
    if (!scheduleModalCrawl) {
      return;
    }

    if (scheduleModalExisting) {
      if (!scheduleModalExisting.id) {
        toast.error({
          title: translate('knowledge.crawlScheduleSaveError'),
          description: '',
        });
        return;
      }

      setIsScheduleSubmitting(true);
      updateCrawlScheduleMutation.mutate(
        {
          databaseId,
          update: {
            id: scheduleModalExisting.id,
            crawlJobId: scheduleModalCrawl.id,
            scheduleInterval: values.scheduleInterval,
            customIntervalHours: values.customIntervalHours,
            active: values.active,
          },
        },
        {
          onSuccess: () => {
            toast.success({
              title: translate('knowledge.crawlScheduleUpdatedTitle'),
              description: '',
            });
            closeScheduleModal();
          },
          onError: (error) => {
            void toastMutationError(
              error,
              translate('knowledge.crawlScheduleSaveError'),
            );
          },
          onSettled: () => {
            setIsScheduleSubmitting(false);
          },
        },
      );
      return;
    }

    setIsScheduleSubmitting(true);
    createCrawlScheduleMutation.mutate(
      {
        databaseId,
        input: {
          crawlJobId: scheduleModalCrawl.id,
          scheduleInterval: values.scheduleInterval,
          customIntervalHours: values.customIntervalHours,
          active: values.active,
        },
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.crawlScheduleCreatedTitle'),
            description: '',
          });
          closeScheduleModal();
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.crawlScheduleSaveError'),
          );
        },
        onSettled: () => {
          setIsScheduleSubmitting(false);
        },
      },
    );
  };

  const handleDeleteSchedule = () => {
    if (!pendingScheduleDelete || isScheduleSubmitting) {
      return;
    }

    setIsScheduleSubmitting(true);
    deleteCrawlScheduleMutation.mutate(
      {
        databaseId,
        scheduleId: pendingScheduleDelete.id,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.crawlScheduleDeletedTitle'),
            description: '',
          });
          setPendingScheduleDelete(null);
          closeScheduleModal();
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.crawlScheduleDeleteError'),
          );
        },
        onSettled: () => {
          setIsScheduleSubmitting(false);
        },
      },
    );
  };

  return {
    openScheduleModal,
    scheduleModalCrawl,
    scheduleModalExisting,
    handleSaveSchedule,
    handleCloseScheduleModal,
    isScheduleSubmitting,
    pendingScheduleDelete,
    setPendingScheduleDelete,
    handleDeleteSchedule,
  };
}
